import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { useLanguage } from "@/contexts/language-context";

interface PricingSectionProps {
  onSelectPlan: (plan: "monthly" | "yearly") => void;
}

export function PricingSection({ onSelectPlan }: PricingSectionProps) {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("yearly");
  const { t } = useLanguage();

  const plans = {
    monthly: {
      price: 14.99,
      period: t.pricing.perMonth,
      savings: null,
    },
    yearly: {
      price: 9.99,
      period: t.pricing.perMonth,
      savings: t.pricing.save,
      billed: t.pricing.billedAnnually
    }
  };

  return (
    <section id="pricing" className="py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-serif text-4xl md:text-5xl font-medium mb-6">
            {t.pricing.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            {t.pricing.subtitle}
          </p>
        </motion.div>

        {/* Billing toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex justify-center mb-10"
        >
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setBillingCycle("monthly")}
              className={`rounded-full ${billingCycle === "monthly" ? "bg-[#101010] text-white hover:bg-[#101010] hover:text-white" : "text-gray-500"}`}
              data-testid="button-billing-monthly"
            >
              {t.pricing.monthly}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setBillingCycle("yearly")}
              className={`rounded-full gap-2 ${billingCycle === "yearly" ? "bg-[#101010] text-white hover:bg-[#101010] hover:text-white" : "text-gray-500"}`}
              data-testid="button-billing-yearly"
            >
              {t.pricing.yearly}
              <Badge variant="secondary" className="bg-[#FF68C3]/10 text-[#FF68C3]">
                {t.pricing.save}
              </Badge>
            </Button>
          </div>
        </motion.div>

        {/* Pricing card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-lg mx-auto"
        >
          <div className="relative">
            {/* Popular badge */}
            {billingCycle === "yearly" && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <Badge className="px-4 py-1 badge-gradient">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t.pricing.mostPopular}
                </Badge>
              </div>
            )}

            <div className="bg-card border rounded-2xl p-8 shadow-lg">
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-serif font-medium">
                    ${plans[billingCycle].price.toFixed(2)}
                  </span>
                  <span className="text-muted-foreground">{plans[billingCycle].period}</span>
                </div>
                {billingCycle === "yearly" && (
                  <p className="text-sm text-muted-foreground mt-2">{plans.yearly.billed}</p>
                )}
              </div>

              <div className="space-y-4 mb-8">
                {t.pricing.features.map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <Button 
                onClick={() => onSelectPlan(billingCycle)}
                size="lg"
                className="w-full btn-gradient"
                data-testid="button-start-ritual-pricing"
              >
                {t.pricing.startRitual}
              </Button>

              <p className="text-center text-sm text-muted-foreground mt-4">
                {t.pricing.trialText}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
